/**
 * CFO Cockpit → Creditors. The mirror of Debtors: what Medicys owes
 * suppliers (open Bills in QuickBooks), aged per vendor, with the
 * upcoming payment obligations and DPO.
 */
import { useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import { fmtGbpShort, PanelTitle } from "../staff/CommissionedPanels";
import { TabState, Card, KpiStrip, InfoTip } from "./cfoShared";
import { useQboData, QboHeader, fmtGbp, fmtMonthKey, fmtDay } from "./qboShared";
import CountrySplit from "./CountrySplit";
import CountryOverrides from "./CountryOverrides";

const fmtExact = (v) =>
  v == null
    ? "–"
    : `£${Number(v).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const AGE_COLS = [
  { key: "current", label: "Current" },
  { key: "b1_30", label: "1–30" },
  { key: "b31_60", label: "31–60" },
  { key: "b61_90", label: "61–90" },
  { key: "b91_plus", label: "91+" },
];

export default function CreditorsTab() {
  const { data, loading, error, refreshing, reload } = useQboData("creditors");
  const [countryVer, setCountryVer] = useState(0);
  if (loading || error) {
    return <TabState loading={loading} error={error} testid="creditors-state" />;
  }
  const k = data.kpis || {};

  return (
    <div className="flex flex-col gap-6" data-testid="creditors-tab">
      <QboHeader data={data} refreshing={refreshing} onRefresh={reload} testid="creditors-refresh-btn" />

      <KpiStrip
        cards={[
          {
            label: "Owed to suppliers",
            value: fmtExact(k.total_ap),
            sub: `including VAT · ${k.open_bills || 0} open bills`,
            testid: "creditors-kpi-ap",
            info: "ap_vat_split",
          },
          {
            label: "Excl VAT",
            value: fmtExact(k.total_ap_net),
            sub: "the net cost still to pay",
            testid: "creditors-kpi-net",
          },
          {
            label: "VAT reclaimable",
            value: fmtExact(k.total_ap_vat),
            sub: "input VAT within open bills",
            testid: "creditors-kpi-vat",
          },
          {
            label: "Overdue",
            value: fmtExact(k.overdue_ap),
            sub: `${k.overdue_pct || 0}% of outstanding`,
            tone: k.overdue_ap > 0 ? "text-rose-600" : "text-slate-900",
            testid: "creditors-kpi-overdue",
          },
          {
            label: "Due next 30 days",
            value: fmtExact(k.due_next_30),
            testid: "creditors-kpi-due30",
          },
          {
            label: "DPO",
            value: k.dpo != null ? `${k.dpo}d` : "–",
            sub: "AP ÷ trailing 12m spend × 365",
            testid: "creditors-kpi-dpo",
            info: "dpo",
          },
        ]}
      />

      <Card testid="creditors-aged-card">
        <PanelTitle>
          Aged payables by supplier <InfoTip id="dpo" />
        </PanelTitle>
        <div className="overflow-x-auto mt-3">
          <table className="w-full text-[11px] border-collapse">
            <thead>
              <tr className="text-[9px] font-mono uppercase tracking-wider text-stone-400 border-b border-stone-200">
                <th className="py-1.5 pr-2 text-left font-medium">Supplier</th>
                {AGE_COLS.map((c) => (
                  <th key={c.key} className="py-1.5 px-2 text-right font-medium">{c.label}</th>
                ))}
                <th className="py-1.5 pl-2 text-right font-medium">Total</th>
              </tr>
            </thead>
            <tbody>
              {data.aged.map((r) => (
                <tr key={r.vendor} className="border-b border-stone-50 last:border-0" data-testid={`creditors-aged-row-${r.vendor}`}>
                  <td className="py-1.5 pr-2 font-medium text-slate-800">{r.vendor}</td>
                  {AGE_COLS.map((c) => (
                    <td
                      key={c.key}
                      className={`py-1.5 px-2 text-right font-mono tabular-nums ${
                        r[c.key] > 0
                          ? c.key === "b91_plus"
                            ? "text-rose-600 font-semibold"
                            : c.key === "current"
                            ? "text-slate-700"
                            : "text-amber-700"
                          : "text-stone-300"
                      }`}
                    >
                      {r[c.key] > 0 ? fmtGbpShort(r[c.key]) : "–"}
                    </td>
                  ))}
                  <td className="py-1.5 pl-2 text-right font-mono tabular-nums font-semibold text-slate-900">
                    {fmtGbp(r.total)}
                  </td>
                </tr>
              ))}
              {data.aged.length === 0 && (
                <tr><td colSpan={7} className="py-4 text-center text-stone-400">Nothing owed to suppliers right now 🎉</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card testid="creditors-upcoming-card">
          <PanelTitle>Upcoming payments</PanelTitle>
          <ul className="divide-y divide-stone-50 mt-2 max-h-80 overflow-y-auto pr-1">
            {(data.upcoming || []).map((u, i) => (
              <li key={`${u.vendor}-${u.due_date}-${i}`} className="py-2 flex items-center gap-3">
                <span className="text-xs text-slate-800 flex-1 truncate">{u.vendor}</span>
                <span className={`text-[10px] font-mono ${u.days < 0 ? "text-rose-600" : "text-stone-400"}`}>
                  {u.days < 0 ? `${-u.days}d overdue` : `due ${fmtDay(u.due_date)}`}
                </span>
                <span className="text-xs font-mono tabular-nums font-semibold text-slate-900">
                  {fmtGbp(u.amount)}
                </span>
              </li>
            ))}
            {(data.upcoming || []).length === 0 && (
              <li className="py-3 text-xs text-stone-400">No open bills.</li>
            )}
          </ul>
        </Card>

        <Card testid="creditors-timeline-card">
          <PanelTitle>Supplier billing · last 12 months</PanelTitle>
          <div className="h-56 mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.timeline || []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <XAxis dataKey="month" tickFormatter={fmtMonthKey} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} />
                <YAxis tickFormatter={(v) => fmtGbpShort(v)} tick={{ fontSize: 10, fill: "#a8a29e" }} axisLine={false} tickLine={false} width={54} />
                <RechartsTooltip
                  contentStyle={{ fontSize: 11, borderRadius: 8 }}
                  labelFormatter={fmtMonthKey}
                  formatter={(v) => [fmtGbp(v), "Billed by suppliers"]}
                />
                <Bar dataKey="billed" fill="#d6a419" maxBarSize={22} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      <CountrySplit
        key={countryVer}
        path="expenditure-by-country"
        title="Expenditure by country · last 12 months"
        infoId="cr_country"
        countKey="suppliers"
        countNoun="supplier"
        testid="expenditure-split-card"
      />

      <CountryOverrides
        kind="vendor"
        noun="supplier"
        onSaved={() => setCountryVer((v) => v + 1)}
        testid="country-overrides-vendor"
      />
    </div>
  );
}
